import { useState, useRef } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import styled from 'styled-components';
import { query } from '@/theme/breakpoints';
import { TextField, Button } from '@/ui';
import { Icon } from '@/icons';
import { useSession } from '@/hooks';
import { Link, useNavigate } from 'react-router-dom';
import { AccountSanctioned, TotpRequired } from '@/core/services/authService';
import { ApiError } from '@/core/lib/apiClient';
import { setupKeyInputError, twoFactorInputError } from '@/core/lib/twoFactorInput';
import { appealsService } from '@/core/services/appealsService';
import { validateSignIn, type FieldErrors } from './validation';

const Form = styled.form`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media ${query.mobile} {
    margin-top: 16px;
  }
`;
/* The one accent fill on the page (see SignUpForm's Submit). */
const Submit = styled(Button)`
  width: 100%;
  height: 44px;
  padding: 0 16px;
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-top: 8px;
`;
const Banner = styled.div`
  color: ${({ theme }) => theme.color.danger};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const Row = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 20px;
  color: ${({ theme }) => theme.color.textMuted};
`;
const Remember = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: ${({ theme }) => theme.color.textPrimary};
  }
`;
const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.color.accentCyan};
  cursor: pointer;
  font: inherit;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;
const ForgotLink = styled(Link)`
  color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none;
  font: inherit;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;
const SanctionPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) =>
    `color-mix(in srgb, ${theme.color.danger} 14%, ${theme.color.glassBg})`};
  border: 1px solid transparent;
`;
const SanctionTitle = styled.p`
  font-weight: 600;
  color: ${({ theme }) => theme.color.danger};
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
const SanctionReason = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const AppealForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textPrimary};
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  resize: vertical;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;
const AppealSuccess = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  /* A status sentence reads as text; the accent as a text colour was a wash. */
  color: ${({ theme }) => theme.color.textPrimary};
`;
const TotpPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const TotpHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const TotpTitle = styled.p`
  font-weight: 600;
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
const TotpHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;
const TotpErrorText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;
// TextField forwards className onto the underlying <input> itself (it spreads
// ...rest, which includes className, onto <Input>), so this styles the input
// directly — no nested "input {}" selector needed.
const CodeInput = styled(TextField)`
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3em;
  text-align: center;
`;

export interface SignInFormProps {
  /** A two-factor challenge handed over by the Google/Apple callback via the
   *  URL (AuthCard strips it on read). Same token login_view would return. */
  initialChallenge?: string | null;
  next: string | null;
}

type AppealState = 'idle' | 'submitted' | 'duplicate' | 'invalid-cred' | 'error';

export function SignInForm({ next, initialChallenge = null }: SignInFormProps) {
  const { signIn, completeTotpSignIn } = useSession();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  // Decorative until B3 — checkbox state is stored locally but not persisted.
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  // Sanction state
  const [sanctionInfo, setSanctionInfo] = useState<{
    status: 'suspended' | 'banned';
    reason: string;
    category: string;
    username: string;
    password: string;
  } | null>(null);
  const [appealReason, setAppealReason] = useState('');
  const [appealState, setAppealState] = useState<AppealState>('idle');
  const [appealBusy, setAppealBusy] = useState(false);

  // 2FA challenge state — the token lives in component state ONLY, never
  // persisted (localStorage, a store, etc.): it's short-lived signed server
  // state good for one sign-in attempt.
  const [challenge, setChallenge] = useState<string | null>(initialChallenge);
  const [totpCode, setTotpCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [totpError, setTotpError] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);
  const totpInFlight = useRef(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner('');
    setSanctionInfo(null);
    setAppealState('idle');
    const errs = validateSignIn({ identifier, password });
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }
    setBusy(true);
    try {
      await signIn(identifier.trim(), password);
      navigate(next ?? '/');
    } catch (err) {
      if (err instanceof TotpRequired) {
        setChallenge(err.challenge);
      } else if (err instanceof AccountSanctioned) {
        setSanctionInfo({
          status: err.sanctionStatus,
          reason: err.reason,
          category: err.category,
          username: identifier.trim().replace(/^@/, ''),
          password,
        });
      } else if (err instanceof Error && err.name === 'RateLimited') {
        setBanner('Too many attempts. Please wait a moment and try again.');
      } else if (err instanceof Error && err.name === 'InvalidCredentials') {
        setBanner('Invalid username or password.');
      } else {
        setBanner('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  function backToPasswordStep(message?: string) {
    setChallenge(null);
    setTotpCode('');
    setUseRecoveryCode(false);
    setTotpError('');
    if (message) setBanner(message);
  }

  async function submitTotp(codeValue: string) {
    const trimmed = codeValue.trim();
    if (!challenge || !trimmed || totpInFlight.current) return;
    const inputError = twoFactorInputError(trimmed, useRecoveryCode);
    if (inputError) {
      setTotpError(inputError);
      return;
    }
    totpInFlight.current = true;
    setTotpBusy(true);
    setTotpError('');
    try {
      await completeTotpSignIn(challenge, trimmed);
      navigate(next ?? '/');
    } catch (err) {
      // Fix-round item 8: a mid-challenge sanction (rare race — the account
      // gets suspended/banned between the password step and completing 2FA)
      // must surface the SAME sanction panel the password step shows, not
      // the generic "code is not correct" message. authService already maps
      // this to AccountSanctioned; this branch was previously missing here.
      if (err instanceof AccountSanctioned) {
        setChallenge(null);
        setSanctionInfo({
          status: err.sanctionStatus,
          reason: err.reason,
          category: err.category,
          username: identifier.trim().replace(/^@/, ''),
          password,
        });
        return;
      }
      const code = (err as { code?: string } | undefined)?.code;
      if (code === 'challenge_expired') {
        backToPasswordStep('That sign-in attempt expired. Please sign in again.');
      } else if (err instanceof Error && err.name === 'RateLimited') {
        setTotpError('Too many attempts. Please wait a moment and try again.');
      } else if (code === 'too_many_attempts') {
        const seconds = err instanceof ApiError ? err.fields?.retryAfter : undefined;
        const minutes =
          typeof seconds === 'number' && Number.isFinite(seconds)
            ? Math.max(1, Math.ceil(seconds / 60))
            : null;
        setTotpError(
          minutes
            ? `Too many failed attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`
            : 'Too many failed attempts. Please wait before trying again.',
        );
      } else if (code === 'invalid_code') {
        setTotpError(
          useRecoveryCode
            ? 'That recovery code is not valid. Use an unused code saved for this account.'
            : 'That code is not correct or has already been used. Check the account in your authenticator app and wait for a new code.',
        );
      } else if (err instanceof ApiError && err.status === 403) {
        backToPasswordStep('Your sign-in session could not be verified. Please sign in again.');
      } else {
        setTotpError(
          'We could not complete verification. Check your connection and try again. If this continues, contact support.',
        );
      }
    } finally {
      totpInFlight.current = false;
      setTotpBusy(false);
    }
  }

  async function handleSubmitTotp(e: FormEvent) {
    e.preventDefault();
    await submitTotp(totpCode);
  }

  function handleTotpCodeChange(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.currentTarget.value;
    const setupError = setupKeyInputError(raw);
    if (setupError) {
      setTotpCode('');
      setTotpError(setupError);
      return;
    }
    setTotpError('');
    if (useRecoveryCode) {
      setTotpCode(raw);
      return;
    }
    const digits = raw.replace(/\s/g, '');
    if (!/^\d{0,6}$/.test(digits)) {
      setTotpCode('');
      setTotpError(
        'Enter only the 6-digit code from your authenticator app. Use the recovery option for a saved recovery code.',
      );
      return;
    }
    setTotpCode(digits);
    if (digits.length === 6) void submitTotp(digits);
  }

  async function handleSubmitAppeal(e: FormEvent) {
    e.preventDefault();
    if (!sanctionInfo || !appealReason.trim()) return;
    setAppealBusy(true);
    try {
      await appealsService.submit({
        username: sanctionInfo.username,
        password: sanctionInfo.password,
        reason: appealReason.trim(),
      });
      setAppealState('submitted');
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'appeal_pending') {
        setAppealState('duplicate');
      } else if (code === 'invalid_credentials') {
        setAppealState('invalid-cred');
      } else {
        setAppealState('error');
      }
    } finally {
      setAppealBusy(false);
    }
  }

  if (challenge) {
    return (
      <TotpPanel>
        <TotpHeader>
          <Icon name="shield" size={18} />
          <TotpTitle>Two-factor verification</TotpTitle>
        </TotpHeader>
        {identifier.trim() && <TotpHint>Signing in as {identifier.trim()}</TotpHint>}
        <TotpHint>
          {useRecoveryCode
            ? 'Enter a saved, unused recovery code for this account, such as XXXX-XXXX-XXXX-XXXX. The setup key belongs in your authenticator app.'
            : 'Enter the 6-digit code from the matching account in your authenticator app. Wait for a new code if you just used one.'}
        </TotpHint>
        <Form as="form" onSubmit={handleSubmitTotp} noValidate>
          {useRecoveryCode ? (
            <TextField
              label="Recovery code"
              value={totpCode}
              aria-invalid={!!totpError}
              autoFocus
              autoComplete="one-time-code"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              disabled={totpBusy}
              onChange={handleTotpCodeChange}
            />
          ) : (
            <CodeInput
              label="6-digit code"
              value={totpCode}
              aria-invalid={!!totpError}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              disabled={totpBusy}
              onChange={handleTotpCodeChange}
            />
          )}
          {/* Fix-round item 9: a plain aria-describedby (what TextField's
              own `error` prop would give us) is only read when the input
              RECEIVES focus — it does not re-announce on a value change
              while focus stays put, which is exactly what happens here (the
              user retypes in the same field after a wrong attempt). A
              dedicated role="alert"/aria-live region re-announces every
              time its text changes, matching the mobile panel's
              accessibilityLiveRegion="polite" behavior. */}
          {totpError && (
            <TotpErrorText role="alert" aria-live="polite">
              {totpError}
            </TotpErrorText>
          )}
          <Button type="submit" variant="primary" disabled={totpBusy || !totpCode.trim()}>
            {totpBusy ? 'Verifying…' : 'Verify'}
          </Button>
        </Form>
        <LinkButton
          type="button"
          disabled={totpBusy}
          onClick={() => {
            setUseRecoveryCode((v) => !v);
            setTotpCode('');
            setTotpError('');
          }}
        >
          {useRecoveryCode ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
        </LinkButton>
        <LinkButton type="button" disabled={totpBusy} onClick={() => backToPasswordStep()}>
          Back to sign in
        </LinkButton>
      </TotpPanel>
    );
  }

  if (sanctionInfo) {
    const title =
      sanctionInfo.status === 'banned'
        ? 'Your account has been banned'
        : 'Your account has been suspended';
    return (
      <div>
        <SanctionPanel role="alert">
          <SanctionTitle>{title}</SanctionTitle>
          {sanctionInfo.reason && (
            <SanctionReason>Reason: {sanctionInfo.reason}</SanctionReason>
          )}
          {sanctionInfo.category && (
            <SanctionReason>Category: {sanctionInfo.category}</SanctionReason>
          )}
        </SanctionPanel>

        {appealState === 'submitted' ? (
          <AppealSuccess role="status">
            Your appeal has been submitted. The owner will review it shortly.
          </AppealSuccess>
        ) : appealState === 'duplicate' ? (
          <AppealSuccess role="status">
            You already have a pending appeal. Please wait for it to be reviewed.
          </AppealSuccess>
        ) : (
          <AppealForm as="form" onSubmit={handleSubmitAppeal}>
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem' }}>
              Appeal this decision
            </p>
            {appealState === 'invalid-cred' && (
              <Banner role="alert">Session expired. Please go back and sign in again.</Banner>
            )}
            {appealState === 'error' && (
              <Banner role="alert">Something went wrong. Please try again.</Banner>
            )}
            <TextArea
              placeholder="Explain why this decision should be reconsidered…"
              value={appealReason}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setAppealReason(e.currentTarget.value)}
              aria-label="Appeal reason"
            />
            <Button type="submit" variant="primary" disabled={appealBusy || !appealReason.trim()}>
              Submit appeal
            </Button>
          </AppealForm>
        )}
        <LinkButton type="button" style={{ marginTop: 12, fontSize: '0.8rem' }} onClick={() => setSanctionInfo(null)}>
          Back to sign in
        </LinkButton>
      </div>
    );
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit} noValidate>
      {banner && <Banner role="alert">{banner}</Banner>}
      <TextField large label="Username or Email" autoComplete="username" placeholder="admin123"
        value={identifier} error={errors.identifier}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setIdentifier(e.currentTarget.value)} />
      <TextField large label="Password" type="password" autoComplete="current-password" placeholder="Your password"
        value={password} error={errors.password}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.currentTarget.value)} />
      <Row>
        <Remember>
          <input type="checkbox" checked={remember}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setRemember(e.currentTarget.checked)} />
          <span>Remember me</span>
        </Remember>
        {/* Was a button toasting "Password reset coming soon". On a live product a
            forgotten password was a permanent lockout — there was no recovery path
            at all, for anyone. */}
        <ForgotLink to="/forgot-password">Forgot password?</ForgotLink>
      </Row>
      <Submit type="submit" variant="primary" disabled={busy}>Login</Submit>
    </Form>
  );
}
